// Styles
import { concatStyleSets, memoizeFunction, mergeStyles } from "@fluentui/react";
import { flexChildSizeFixed, flexChildSizeFlexible, flexParent, fontColorNeutralPrimary, styleButtonAsDiv, fontColorThemePrimary, textClip } from "../../../utilities/Mixin.styles";
// Utilities
import { isLoopTheme } from "../../../utilities/ThemingUtilities";
/**
 * Task Assignment Control Style constants
 */ const assignIconSize = "20px";
const compactAssignIconMargin = "6px";
const regularAssignIconMargin = "10px";
const regularAssignIconVerticalMargin = "10px";
const itemButtonHeight = "24px";
/**
 * Helper to get styles for the task assignment control
 * @param theme The theme to render the control
 * @param customStyles Any custom style overrides
 * @param isReadonly Whether the control is in readonly mode
 * @param isCompact Whether the control should render compact (preserving height)
 * @param isIconBefore Whether the add friend icon is before the facepile
 * @param allowMultiline Whether the facepile can render in multiple lines if needed
 */ export const getStyles = memoizeFunction((theme, customStyles, isReadonly, isCompact, isIconBefore, allowMultiline)=>{
    const isLoop = isLoopTheme(theme);
    const TaskAssignmentControlStyles = {
        root: [
            flexParent("column"),
            {
                cursor: "pointer"
            },
            isReadonly && {
                cursor: "default"
            }
        ],
        assigneeRow: [
            styleButtonAsDiv,
            flexParent(),
            flexChildSizeFlexible("auto"),
            {
                position: "relative",
                cursor: "pointer",
                alignItems: "center",
                ".hidePersonaDetails .ms-Persona-details": {
                    display: "none"
                },
                ".ms-Persona-details": {
                    paddingRight: "0px"
                },
                ".ms-Persona-primaryText": [
                    fontColorNeutralPrimary(theme),
                    {
                        margin: "0px"
                    }
                ],
                ".ms-Facepile": [
                    flexChildSizeFlexible(),
                    flexParent(),
                    {
                        margin: "0px"
                    }
                ],
                ".ms-Facepile-itemContainer": [
                    flexChildSizeFlexible(),
                    flexParent(),
                    textClip(),
                    {
                        alignItems: "center",
                        "> button": flexChildSizeFixed()
                    }
                ],
                ".ms-Facepile-member": flexChildSizeFixed(),
                ".ms-Facepile-itemButton": [
                    flexChildSizeFixed(),
                    {
                        height: itemButtonHeight,
                        ".ms-Image > img": {
                            pointerEvents: "none"
                        }
                    }
                ],
                "> span": flexChildSizeFixed(),
                ".ms-Facepile-addButton i": isLoop ? fontColorNeutralPrimary(theme) : fontColorThemePrimary(theme),
                ".ms-Facepile-itemContainer .ms-Facepile-descriptiveOverflowButton.ms-Facepile-itemButton": {
                    marginLeft: "4px",
                    marginRight: "4px"
                }
            },
            allowMultiline && {
                ".ms-Facepile-members": [
                    flexChildSizeFlexible(),
                    flexParent("row", "wrap")
                ]
            },
            !allowMultiline && {
                ".ms-Facepile-members": [
                    flexChildSizeFixed(),
                    flexParent("row", "nowrap")
                ]
            },
            isReadonly && {
                cursor: "default"
            }
        ],
        addFriendIcon: [
            flexChildSizeFixed(assignIconSize),
            flexParent(),
            fontColorNeutralPrimary(theme),
            {
                marginLeft: "0px",
                borderRadius: "16px",
                margin: "5px 0px",
                justifyContent: "center",
                alignItems: "center",
                fontStyle: "normal",
                width: "16px"
            },
            isIconBefore && {
                marginRight: isCompact ? compactAssignIconMargin : regularAssignIconMargin
            },
            !isIconBefore && {
                justifyContent: "space-between",
                marginLeft: isCompact ? compactAssignIconMargin : regularAssignIconMargin
            },
            !isCompact && {
                marginTop: regularAssignIconVerticalMargin,
                marginBottom: regularAssignIconVerticalMargin
            }
        ],
        selfAssign: [
            styleButtonAsDiv,
            flexChildSizeFixed(),
            {
                borderWidth: "0px",
                marginLeft: "30%",
                transition: "background 0.167s ease-in-out",
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
            },
            !isReadonly && {
                "&:hover": [
                    fontColorThemePrimary(theme)
                ]
            }
        ],
        assignDiv: [
            styleButtonAsDiv,
            flexChildSizeFixed(),
            {
                borderWidth: "0px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer"
            }
        ]
    };
    return concatStyleSets(TaskAssignmentControlStyles, customStyles);
});
/**
 * Get the classnames associated with the computed styles for the task assignment control component
 * @param styles The styles for the component
 */ export const getClassNames = memoizeFunction((styles)=>{
    return {
        root: mergeStyles("taskAssignmentControl", styles.root),
        assigneeRow: mergeStyles("assigneeRow", styles.assigneeRow),
        addFriendIcon: mergeStyles("addFriendIcon", styles.addFriendIcon),
        selfAssign: mergeStyles("selfAssign", styles.selfAssign),
        assignButton: mergeStyles("assignButton", styles.assignButton),
        assignDiv: mergeStyles("assignDiv", styles.assignDiv),
        assignLabel: mergeStyles("assignLabel", styles.assignLabel)
    };
});
